import { useEffect, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, CircleInfoFill, Plus } from '@gravity-ui/icons';
import { Button, Icon, TextArea } from '@gravity-ui/uikit';
import { FormRow } from '@gravity-ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/query';

import { useSafeParams } from 'hooks';
import { useGetBlankQuery } from 'store/api';
import type { Problem } from 'store/types/common';

import {
	ADD_DESCRIPTION,
	BACK,
	CLIENT_DAY,
	EVERYDAY_ACTIVITY,
	EXPECTED,
	HINTS,
	HINTS_QUESTION,
	LEISURE,
	NEEDED,
	PROBLEM_AREAS,
	PROBLEM_TITLE,
	PRODUCTIVE_ACTIVITY,
	SAVE,
	SELF_SERVICE,
	WANTS,
} from '../constants';

import { Loader } from './utils/load-skeleton';
import { ProblemBlock } from './utils/problem-block';
import { ProblemModal } from './utils/problem-modal';
import type { StepTwoInfo } from './utils/use-validation';
import { useStepTwoValidation } from './utils/use-validation';
import { useMediaQuery } from '@mui/material';
import styles from './styles.module.scss';

interface StepTwoFormProps {
	active: number;
	onStepComplete: (data: StepTwoInfo) => void;
	prevStep: () => void;
	isMobile: any;
}

export const StepTwoForm = ({ active, prevStep, onStepComplete, isMobile }: StepTwoFormProps) => {
	const [isHelpListVisible, setHelpListVisible] = useState(true);

	const [openProblemModal, setOpenProblemModal] = useState(false);

	const isAdaptive = useMediaQuery('(max-width: 768px)');

	const { stepTwoSchema } = useStepTwoValidation();

	const { clientId } = useSafeParams();
	const { data, isLoading, isSuccess, refetch } = useGetBlankQuery(clientId ?? skipToken, {
		refetchOnMountOrArgChange: 1,
	});

	useEffect(() => {
		if (data) {
			console.log('шаг 2', data);
		}
	}, [data]);

	const defaultProblems = [
		{
			text: '',
			importance: 0,
			parentSectionName: '',
			subsection: { sectionName: '' },
			now: '',
			shortTermGoals: [],
		},
		{
			text: '',
			importance: 0,
			parentSectionName: '',
			subsection: { sectionName: '' },
			now: '',
			shortTermGoals: [],
		},
	];

	const methods = useForm<StepTwoInfo>({
		resolver: zodResolver(stepTwoSchema),
		defaultValues: {
			problems:
				data?.problems && data.problems.length > 0
					? data.problems.map((problem: Problem) => ({
							id: problem.id ? Number(problem.id) : undefined,
							text: problem.text || '',
							importance: problem.importance || 0,
							parentSectionName: problem.parentSectionName || '',
							subsection: { sectionName: problem.subsection?.sectionName || '' },
							now: problem.now || '',
							shortTermGoals: problem.shortTermGoals || [],
						}))
					: defaultProblems,
		},
		mode: 'onChange',
	});

	const {
		handleSubmit,
		watch,
		control,
		trigger,
		formState: { errors, isValid, isSubmitted },
	} = methods;

	const formValues = watch();

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'problems',
	});

	const toggleHelpListVisibility = () => {
		setHelpListVisible(!isHelpListVisible);
	};

	const handleOpenModal = async () => {
		const isValid = await trigger();
		if (!isValid) {
			console.log('Ошибки валидации:', errors);
			return;
		}
		setOpenProblemModal(true);
	};

	const onSubmit = (data: StepTwoInfo) => {
		onStepComplete(data);
		setOpenProblemModal(false);
	};

	useEffect(() => {
		if (data) {
			const problems =
				data.problems && data.problems.length > 0
					? data.problems.map((problem: Problem) => ({
							id: problem.id ? Number(problem.id) : undefined,
							text: problem.text || '',
							importance: problem.importance || 0,
							parentSectionName: problem.parentSectionName || '',
							subsection: { sectionName: problem.subsection?.sectionName || '' },
							now: problem.now || '',
							shortTermGoals: problem.shortTermGoals || [],
						}))
					: defaultProblems;

			methods.reset({
				problems,
			});
		}
	}, [data]);

	return (
		<>
			{isLoading && <Loader />}

			{!isLoading && (
				<div className={styles.container}>
					{isMobile ? <h3 className={styles.info__title}>{PROBLEM_TITLE}</h3> : ''}
					<div className={`${styles.help} ${isHelpListVisible ? '' : styles.hidden}`}>
						<div className={styles.help__title}>
							<CircleInfoFill className={styles.help__title__svg} />
							<h4>{HINTS}</h4>
							<Button onClick={toggleHelpListVisibility} view="flat">
								{' '}
								{isHelpListVisible ? <ChevronDown /> : <ChevronUp />}
							</Button>
						</div>
						<div className={`${styles.help__list} ${isHelpListVisible ? '' : styles.hidden}`}>
							<ol>
								<li>
									{PROBLEM_AREAS}
									<ul>
										<li>{SELF_SERVICE.toLowerCase()};</li>
										<li>{PRODUCTIVE_ACTIVITY.toLowerCase()};</li>
										<li>{LEISURE.toLowerCase()}.</li>
									</ul>
								</li>
								<li>{CLIENT_DAY}</li>
								<li>
									{EVERYDAY_ACTIVITY}
									<ul>
										<li>{WANTS};</li>
										<li>{NEEDED};</li>
										<li>{EXPECTED}.</li>
									</ul>
								</li>
								<li>{HINTS_QUESTION}</li>
							</ol>
						</div>
					</div>
					<div className={styles.clientRequestBlock}>
						<FormRow label="Запрос клиента" className={styles.row} required>
							<TextArea
								value={data?.customerRequest || ''}
								minRows={6}
								note={<span>{(data?.customerRequest || '').length}/2000</span>}
								readOnly
								className={styles.clientRequestTextArea}
							/>
						</FormRow>
						<div className={styles.info}>
							{isMobile ? '' : <h3 className={styles.info__title}>{PROBLEM_TITLE}</h3>}

							<FormProvider {...methods}>
								<form onSubmit={handleSubmit(onSubmit)}>
									<div className={styles.clientRequestBlock}></div>

									{fields.map((field: any, index: number) => (
										<ProblemBlock
											key={field.id}
											index={index}
											removeBlock={() => {
												remove(index);
											}}
											methods={methods}
											errors={errors}
											active={active}
											fields={fields}
											append={append}
											isAdaptive={isAdaptive}
											isMobile={isMobile}
										/>
									))}

									<Button
										size="xl"
										className={styles.info__btn_description}
										onClick={() =>
											append({
												text: '',
												importance: 0,
												parentSectionName: '',
												subsection: { sectionName: '' },
											})
										}
										view="outlined"
									>
										<Icon data={Plus} size={20} />
										{ADD_DESCRIPTION}{' '}
									</Button>

									<div className={styles.info__btn_group}>
										{active !== 0 && (
											<>
												{isAdaptive ? (
													<Button onClick={prevStep} size="xl">
														<Icon data={ArrowLeft} size={18} />
													</Button>
												) : (
													<Button onClick={prevStep} size="xl">
														<Icon data={ArrowLeft} size={18} />
														{BACK}
													</Button>
												)}
											</>
										)}
										{active !== 3 && (
											<Button
												size="xl"
												onClick={handleOpenModal}
												//disabled={!isValid}
												// view={isValid ? 'action' : 'raised'}
												className={styles.info__btn_group__save}
											>
												{SAVE} <Icon data={ArrowRight} size={18} />{' '}
											</Button>
										)}
										<ProblemModal
											openProblemModal={openProblemModal}
											setOpenProblemModal={setOpenProblemModal}
											onSubmit={handleSubmit(onSubmit)}
											problems={formValues.problems.map((p) => ({
												...p,
											}))}
										/>
									</div>
								</form>
							</FormProvider>
						</div>
					</div>
				</div>
			)}
		</>
	);
};
